import React, { useMemo } from 'react';
import { EducationalTip, EducationalTipProps } from 'components/ui/educational-tip';

export interface TipData {
	/** Un icono de lucide (Proa v0.13.0): antes era un emoji. */
	icon: EducationalTipProps['icon'];
	title: string;
	text: string;
	cta: string;
}

export interface EducationalTipV2Props {
	tips: TipData[];
	onCtaClick?: (tip: TipData) => void;
}

/**
 * El pintado vive en `ui/educational-tip` (Proa). Lo que se queda aquí es lo único que el sistema
 * no debe saber: que esta pantalla tiene VARIOS tips y enseña uno al azar en cada visita. Un
 * componente de sistema recibe el tip ya resuelto — si eligiera él, sería no determinista, y con
 * eso la regresión visual no podría comparar nada.
 *
 * Al adoptarlo cambió el aspecto, a propósito (decisión de Marisa, 27/09/26): la CTA pasó de estar
 * dentro de la columna de texto con una bombilla a la derecha con un chevron. Desde Proa v0.13.0 el
 * tip lleva un icono de lucide en un `IconBadge` redondo (antes, un emoji) y la tarjeta es la
 * `elevated` del sistema, blanca con borde y sombra.
 *
 * Y se va `framer-motion`: el sistema entra con CSS, no con JS. Aquí eso significa perder el
 * `fade` de 0,5 s de retardo, que la red visual no veía de todos modos porque mide en reposo.
 */
const EducationalTipV2: React.FC<EducationalTipV2Props> = ({ tips, onCtaClick }) => {
	const currentTip = useMemo(
		() => tips[Math.floor(Math.random() * tips.length)],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[] // Only pick once per mount
	);

	if (!currentTip) return null;

	return (
		<EducationalTip
			icon={currentTip.icon}
			title={currentTip.title}
			text={currentTip.text}
			ctaLabel={currentTip.cta}
			onCtaClick={onCtaClick && (() => onCtaClick(currentTip))}
		/>
	);
};

export default EducationalTipV2;
